import { useState, type FormEvent } from 'react';
import { LIMITS, displayTag } from '@engineer-os/chat';
import { useStrings } from '../../state/useLocale';
import { Button, Machine } from '../../ui';
import type { ChatRow } from '../../state/useChat';
import { RichText } from './CustomEmoji';
import { Identity } from './Identity';

/**
 * Reporting a message to whoever runs the deployment.
 *
 * It takes the panel over rather than floating above the conversation, and
 * that is the same decision the rename form makes: a report is a deliberate
 * stop, not a thing done in passing, and the person doing it should be looking
 * at one screen with the message on it. The message is quoted back at the top
 * for the same reason — a moderation queue full of reports about the wrong
 * line is worse than an empty one.
 *
 * The sentence about what happens next is the point of this dialog. Nobody at
 * EngineerOS reads these; the reports go into the deployment's own database
 * and whoever runs it reads them, and they see the message, the reporter's tag
 * and whatever is typed here. Saying so plainly is cheaper than the alternative
 * — somebody reporting a classmate under the impression that a company is
 * about to arrive — and it is the same honesty the rest of the product's
 * security writing uses about what is and is not built.
 *
 * The reason is optional and is refused for nothing. The room cleans it for
 * length and control characters and drops it if there is nothing usable left,
 * because a report with an unusable reason is still a report and losing the
 * report would be the worse mistake (packages/chat, `report`).
 *
 * The confirmation is optimistic, and it is worded to be true anyway. A report
 * is answered by the absence of an error — there is no acknowledgement frame,
 * deliberately, because an acknowledgement is a thing a room would have to
 * broadcast — so "Reported. Nothing else happens here." is exactly what the
 * tab knows: it sent it, and nothing further will visibly happen.
 */
export interface ReportDialogProps {
  row: ChatRow;
  /** Sends it. There is no reply frame; the absence of an error is the answer. */
  onSend(reason: string | undefined): void;
  onClose(): void;
}

export function ReportDialog({ row, onSend, onClose }: ReportDialogProps) {
  const s = useStrings().chat;
  const [reason, setReason] = useState('');
  const [sent, setSent] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = reason.trim();
    onSend(trimmed === '' ? undefined : trimmed);
    setSent(true);
  };

  return (
    <form className="chat-report" onSubmit={submit}>
      <p className="label">{s.report.title}</p>

      <div className="chat-report-quote">
        <span className="label">{s.report.quoted}</span>
        <div className="msg-head">
          <Identity m={row} />
          {/* Display the short label; the report retains the full identity. */}
          <Machine className="msg-tag">#{displayTag(row.tag)}</Machine>
        </div>
        <div className="msg-text" dir="auto">
          <RichText text={row.text} />
        </div>
      </div>

      {sent ? (
        <>
          <p className="note" role="status">
            {s.report.sent}
          </p>
          <div className="chat-report-actions">
            <Button type="button" variant="solid" onClick={onClose}>
              {s.report.close}
            </Button>
          </div>
        </>
      ) : (
        <>
          <label className="label" htmlFor="chat-report-why">
            {s.report.reason}
          </label>
          <textarea
            id="chat-report-why"
            className="chat-input"
            rows={3}
            value={reason}
            maxLength={LIMITS.text}
            autoComplete="off"
            autoFocus
            // Either script, like every other field in this panel that holds
            // something a person wrote.
            dir="auto"
            aria-describedby="chat-report-note"
            onChange={(e) => setReason(e.target.value)}
          />
          <p id="chat-report-note" className="note">
            {s.report.reasonNote}
          </p>
          <p className="note">{s.report.what}</p>
          <div className="chat-report-actions">
            <Button type="submit" variant="solid">
              {s.report.send}
            </Button>
            <Button type="button" variant="quiet" onClick={onClose}>
              {s.report.cancel}
            </Button>
          </div>
        </>
      )}
    </form>
  );
}

export default ReportDialog;
